import { CardErrorReason, EnrichedCardDetails } from './checkout'
import { ResultCode } from '@afterpaytouch/topaz-api'
import { InitiateThreeDSErrorCode, TopazStatus } from '.'
import { isNullOrUndefined } from '@afterpay/utils'

export enum OverdueType {
  ExistingPayment,
  NewPayment,
}

export const TOPAZ_ERROR_REASONS = {
  UnacceptableIssuerRestrictedCard: 'unacceptableIssuerRestrictedCard',
  UnacceptableCard: 'unacceptableCard',
  CashAppCardIneligible: 'cashAppCardIneligible',
} as const

type TopazErrorReasonsType = typeof TOPAZ_ERROR_REASONS
type TopazErrorReasonsKeys = keyof TopazErrorReasonsType
export type TopazCardErrorReason = TopazErrorReasonsType[TopazErrorReasonsKeys]

export const topazResultCodeErrors: Partial<Record<ResultCode, TopazStatus>> = {
  [ResultCode.OK]: TopazStatus.Saved,
  [ResultCode.DUPLICATE_CARD]: TopazStatus.DuplicateCard,
  [ResultCode.INVALID_DETAILS]: TopazStatus.InvalidDetails,
  [ResultCode.EXCEEDED_MAX_ATTEMPTS_ENTERING_DETAILS]: TopazStatus.ExceededMaxAttemptsEnteringDetails,
  [ResultCode.UNACCEPTABLE_CARD]: TopazStatus.UnacceptableCard,
  [ResultCode.INVALID_STATE]: TopazStatus.InvalidState,
  [ResultCode.INVALID_SHIPPING_DETAILS]: TopazStatus.InvalidShippingDetails,
  [ResultCode.TRANSIENT_GATEWAY_ERROR]: TopazStatus.TransientGatewayError,
  [ResultCode.INSUFFICIENT_FUNDS]: TopazStatus.InsufficientFunds,
  [ResultCode.OTHER_ERROR]: TopazStatus.OtherError,
  [ResultCode.USER_CHALLENGE_FAILED]: TopazStatus.UserChallengeFailed,
  [ResultCode.INVALID_REQUEST_PARAMETERS]: TopazStatus.InvalidRequestDetails,
  [ResultCode.VELOCITY_RULE_BREACH]: TopazStatus.VelocityRuleBreach,
  [ResultCode.FRAUD_RULE_BREACH]: TopazStatus.FraudRuleBreach,
  [ResultCode.REQUIRE_THREE_DS_AUTHENTICATION]: TopazStatus.RequireThreeDsAuthentication,
  [ResultCode.THREE_DS_CHALLENGE_PENDING]: TopazStatus.ThreeDSChallengePending,
}

export const topazErrorReasonCodes: Partial<Record<TopazStatus, CardErrorReason>> = {
  [TopazStatus.UnacceptableCard]: TOPAZ_ERROR_REASONS.UnacceptableCard,
  [TopazStatus.UnacceptableCardIssuerRestricted]: TOPAZ_ERROR_REASONS.UnacceptableIssuerRestrictedCard,
  [TopazStatus.CashAppCardIneligible]: TOPAZ_ERROR_REASONS.CashAppCardIneligible,
}

export const PAYMENT_METHOD_ERROR_REASONS = {
  InvalidDetails: 'default:cardNumber',
  UnacceptableCard: 'unsupportedCard',
  UnacceptableCardIssuerRestricted: 'unsupportedCardIssuerRestricted',
  FailedToRetrieve: 'failedToRetrieveCard',
  Unknown: 'unknown',
  PaymentRequired: 'paymentRequired',
  PreconditionFailed: 'preConditionFailed',
  DuplicateCard: 'duplicateCard',
  CashAppCardIneligible: 'cashAppCardIneligibleTopaz',
}

type PaymentMethodErrorReasonsType = typeof PAYMENT_METHOD_ERROR_REASONS
type PaymentMethodErrorReasonsKeys = keyof PaymentMethodErrorReasonsType
export type PaymentMethodCardErrorReason = PaymentMethodErrorReasonsType[PaymentMethodErrorReasonsKeys]

export const paymentMethodTopazErrorReason: Partial<Record<TopazStatus, PaymentMethodCardErrorReason>> = {
  [TopazStatus.InvalidDetails]: PAYMENT_METHOD_ERROR_REASONS.InvalidDetails,
  [TopazStatus.DuplicateCard]: PAYMENT_METHOD_ERROR_REASONS.DuplicateCard,
  [TopazStatus.UnacceptableCard]: PAYMENT_METHOD_ERROR_REASONS.UnacceptableCard,
  [TopazStatus.UnacceptableCardIssuerRestricted]: PAYMENT_METHOD_ERROR_REASONS.UnacceptableCardIssuerRestricted,
  [TopazStatus.CashAppCardIneligible]: PAYMENT_METHOD_ERROR_REASONS.CashAppCardIneligible,
  [TopazStatus.FailedToRetrieveNewCard]: PAYMENT_METHOD_ERROR_REASONS.Unknown,
  [TopazStatus.InvalidState]: PAYMENT_METHOD_ERROR_REASONS.Unknown,
  [TopazStatus.ExceededMaxAttemptsEnteringDetails]: PAYMENT_METHOD_ERROR_REASONS.Unknown,
  [TopazStatus.Error]: PAYMENT_METHOD_ERROR_REASONS.Unknown,
}

export const paymentMethodThreeDSErrorReason: Partial<Record<InitiateThreeDSErrorCode, PaymentMethodCardErrorReason>> = {
  [InitiateThreeDSErrorCode.PAYMENT_REQUIRED]: PAYMENT_METHOD_ERROR_REASONS.PaymentRequired,
  [InitiateThreeDSErrorCode.PRECONDITION_FAILED]: PAYMENT_METHOD_ERROR_REASONS.PreconditionFailed,
}

export const preferredCardTopazErrorReasons: CardErrorReason[] = [TOPAZ_ERROR_REASONS.UnacceptableCard, TOPAZ_ERROR_REASONS.UnacceptableIssuerRestrictedCard]
export const hasCardTopazErrorReason = (card: EnrichedCardDetails | null | undefined): boolean =>
  !isNullOrUndefined(card) && !isNullOrUndefined(card.error) && preferredCardTopazErrorReasons.includes(card.error)
